import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MDRBadge } from "@/components/MDRBadge";
import {
  pharmaciesData, diagnosticRoomsData, bookingsData, consumablesData, campaignsData,
  getPharmacyById
} from "@/data/platformData";
import { servicesStats } from "@/data/mockData";
import { ManagerDeviceAlertsCard, getPharmacyDeviceAlerts } from "@/components/manager/ManagerDeviceAlerts";
import {
  Building2, DoorOpen, Calendar, Package, AlertTriangle, Megaphone, Plus,
  TrendingUp, Users, ClipboardList, Cpu
} from "lucide-react";

export const ManagerHomePage = () => {
  const navigate = useNavigate();
  // Mock: this manager handles ph1
  const pharmacy = getPharmacyById("ph1");
  const rooms = diagnosticRoomsData.filter(r => r.pharmacyId === "ph1");
  const bookings = bookingsData.filter(b => b.pharmacyId === "ph1");
  const stock = consumablesData.filter(c => c.pharmacyId === "ph1");
  const lowStock = stock.filter(s => s.stock < s.threshold);
  const campaigns = campaignsData.filter(c => c.pharmacyIds.includes("ph1"));
  const deviceAlerts = getPharmacyDeviceAlerts("ph1");
  const deviceAlertCount = deviceAlerts.length;

  const kpis = [
    { label: "Sale diagnostiche", value: rooms.length, icon: DoorOpen, accent: "indigo" },
    { label: "Prenotazioni attive", value: bookings.length, icon: Calendar, accent: "sky" },
    { label: "Alert dispositivi", value: deviceAlertCount, icon: Cpu, accent: deviceAlertCount > 0 ? "rose" : "emerald" },
    { label: "Scorte sotto soglia", value: lowStock.length, icon: AlertTriangle, accent: "rose" }
  ];

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="h-4 w-4 text-indigo-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Responsabile Farmacia</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{pharmacy?.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{pharmacy?.address}</p>
        </div>
        <Button
          variant="premium"
          onClick={() => navigate("/booking?from=pharmacy_manager&pharmacyId=ph1")}
          data-testid="manager-new-booking-btn"
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Nuova prenotazione
        </Button>
      </div>

      {/* KPI grid */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {kpis.map(k => {
          const Icon = k.icon;
          const colorMap = {
            indigo: "from-indigo-500/10 to-indigo-500/5 text-indigo-700",
            sky: "from-sky-500/10 to-sky-500/5 text-sky-700",
            emerald: "from-emerald-500/10 to-emerald-500/5 text-emerald-700",
            rose: "from-rose-500/10 to-rose-500/5 text-rose-700"
          };
          return (
            <Card key={k.label} className={`bg-gradient-to-br ${colorMap[k.accent]} border-2`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/60 dark:bg-black/20 flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold leading-none">{k.value}</p>
                  <p className="text-xs mt-1 opacity-80">{k.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Stato dispositivi (Health Check) */}
        <Card className={deviceAlertCount > 0 ? "lg:col-span-2 border-2 border-rose-100" : "lg:col-span-2"} data-testid="manager-devices-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Cpu className={`h-4 w-4 ${deviceAlertCount > 0 ? "text-rose-600" : "text-emerald-600"}`} />
                Stato dispositivi
                {deviceAlertCount > 0 && (
                  <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300 ml-1">{deviceAlertCount} alert</Badge>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/manager/monitoring")} data-testid="manager-go-devices">
                Tutti i dispositivi
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ManagerDeviceAlertsCard pharmacyId="ph1" />
          </CardContent>
        </Card>

        {/* Sale diagnostiche */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <DoorOpen className="h-4 w-4 text-indigo-600" /> Sale diagnostiche
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {rooms.map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div>
                  <p className="font-medium text-sm">{r.name}</p>
                  <p className="text-xs text-muted-foreground">Kit {r.kitId} · {r.capabilities.length} prestazioni</p>
                </div>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Aperta</Badge>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate("/manager/planning")} data-testid="manager-go-planning">
              Gestisci aperture
            </Button>
          </CardContent>
        </Card>

        {/* Scorte */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4 text-rose-600" /> Scorte consumabili
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {stock.slice(0, 4).map(s => {
              const isLow = s.stock < s.threshold;
              return (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div>
                    <p className="font-medium text-sm">{s.name}</p>
                    <p className="text-xs text-muted-foreground">Soglia {s.threshold} {s.unit}</p>
                  </div>
                  <Badge variant="outline" className={isLow ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                    {s.stock} {s.unit}
                  </Badge>
                </div>
              );
            })}
            <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate("/manager/inventory")} data-testid="manager-go-inventory">
              Gestisci scorte
            </Button>
          </CardContent>
        </Card>

        {/* Campagne */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-amber-600" /> Campagne promozionali
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {campaigns.map(c => (
              <div key={c.id} className="p-3 rounded-lg border bg-card">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm">{c.name}</p>
                  <Badge variant={c.status === "active" ? "default" : "secondary"} className="text-[10px] uppercase">
                    {c.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {c.period.start} → {c.period.end} · -{c.discount}%
                </p>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate("/manager/campaigns")} data-testid="manager-go-campaigns">
              Gestisci campagne
            </Button>
          </CardContent>
        </Card>

        {/* Top prestazioni */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" /> Prestazioni più richieste
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {servicesStats.slice(0, 5).map(s => (
              <div key={s.name} className="flex items-center justify-between text-sm">
                <span className="truncate">{s.name}</span>
                <Badge variant="secondary" className="font-mono">{s.count}</Badge>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate("/manager/monitoring")} data-testid="manager-go-monitoring">
              Vedi monitoraggio completo
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="grid gap-3 md:grid-cols-3">
        <Card className="hover:shadow-md transition cursor-pointer" onClick={() => navigate("/manager/bookings")} data-testid="manager-card-bookings">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">Prenotazioni</p>
              <p className="text-xs text-muted-foreground">Sposta o annulla appuntamenti</p>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition cursor-pointer" onClick={() => navigate("/manager/configuration")} data-testid="manager-card-config">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">Configurazione</p>
              <p className="text-xs text-muted-foreground">Tempi, tariffe, operatori</p>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition cursor-pointer" onClick={() => navigate("/manager/monitoring")} data-testid="manager-card-monitoring">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">Monitoraggio</p>
              <p className="text-xs text-muted-foreground">KPI, dispositivi, fatturato</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ManagerHomePage;
